import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { Select, MenuItem, SelectChangeEvent, Collapse, Checkbox, FormControlLabel } from '@mui/material';
import AppTheme from '../shared-theme/AppTheme';
import { styled } from '@mui/material/styles';
// import { SitemarkIcon } from './CustomIcons';
import axios from 'axios';
// import ConnectGlowCard from './ConnectGlow';
import ConnectOctopusCard from './ConnectOctopus';
import ApiClient from '../utils/apiClient';
import Copyright from '../dashboard/internals/components/Copyright';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  maxWidth: '600px',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.surface,
  [theme.breakpoints.down('sm')]: {    
    padding: theme.spacing(2), // Reduce padding on xs/sm screens
    borderStyle: 'none',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignUpContainer = styled(Stack)(({ theme }) => ({
  padding: 20,
  [theme.breakpoints.down('sm')]: {
    padding: '20px 0', // Remove x padding on xs/sm screens
    backgroundColor: 'transparent', // Make container background transparent on xs/sm screens
  },
  minHeight: '100vh',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage: theme.palette.mode === 'light' 
      ? 'url(/light-bg.jpg)' 
      : 'url(/dark-bg.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed'
  },
}));

export default function Register(props: { disableCustomTheme?: boolean }) {
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [confirmPasswordError, setConfirmPasswordError] = React.useState(false);
  const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = React.useState('');
  const [firstNameError, setFirstNameError] = React.useState(false);
  const [firstNameErrorMessage, setFirstNameErrorMessage] = React.useState('');
  const [lastNameError, setLastNameError] = React.useState(false);
  const [lastNameErrorMessage, setLastNameErrorMessage] = React.useState('');
  const [energyProvider, setEnergyProvider] = React.useState<string>('');
  const [registrationComplete, setRegistrationComplete] = React.useState<boolean>(false);
  const [isConnected, setIsConnected] = React.useState<boolean>(false);
  const [termsAccepted, setTermsAccepted] = React.useState<boolean>(false);
  const [registrationError, setRegistrationError] = React.useState<string>('');
  const [isSigningUp, setIsSigningUp] = React.useState<boolean>(false);

  const handleEnergyProviderChange = (event: SelectChangeEvent<string>) => {
    setEnergyProvider(event.target.value);
  };

  const handleConnectionChange = (connected: boolean) => {
    setIsConnected(connected);
  };

  const validateInputs = () => {
    const email = document.getElementById('email') as HTMLInputElement;
    const password = document.getElementById('password') as HTMLInputElement;
    const confirmPassword = document.getElementById('confirm_password') as HTMLInputElement;
    const firstName = document.getElementById('first_name') as HTMLInputElement;
    const lastName = document.getElementById('last_name') as HTMLInputElement;

    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password.value || password.value.length < 8 || password.value.length > 32) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be between 8 and 32 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
      
      if (!confirmPassword.value || confirmPassword.value !== password.value) {
        setConfirmPasswordError(true);
        setConfirmPasswordErrorMessage('Password must match the one given above.');
        isValid = false;
      } else {
        setConfirmPasswordError(false);
        setConfirmPasswordErrorMessage('');
      }

    }
    

    if (!firstName.value || firstName.value.length < 1) {
      setFirstNameError(true);
      setFirstNameErrorMessage('First name is required.');
      isValid = false;
    } else {
      setFirstNameError(false);
      setFirstNameErrorMessage('');
    }
    
    if (!lastName.value || lastName.value.length < 1) {
      setLastNameError(true);
      setLastNameErrorMessage('Last name is required.');
      isValid = false;
    } else {
      setLastNameError(false);
      setLastNameErrorMessage('');
    }

    return isValid;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Clear any previous registration error
    setRegistrationError('');
    setIsSigningUp(true);

    if (validateInputs()) {
      const data = new FormData(event.currentTarget);
      const details = Object.fromEntries(data);

      // Convert FormData to properly typed object for JSON
      const requestData = {
        first_name: String(details.first_name).trim(),
        last_name: String(details.last_name).trim(),
        email: String(details.email).trim(),
        password: details.password,
        confirm_password: details.confirm_password
      };

      try {
        const requestOptions = {
          headers: { 
            'Content-Type': 'application/json'
          }
        };
        axios.post(`${ApiClient.getCurrentBaseUrl()}/api/users/register`, requestData, requestOptions)
            .then(response => {
              localStorage.setItem("token", response.data['token']);
              localStorage.setItem("user_id", response.data["user"]["user_id"]);
              setRegistrationComplete(true);
              setIsSigningUp(false);
            })
            .catch(function (error) {
              setIsSigningUp(false);
              if (error.response) {
                console.log(error.response.data);
                console.log(error.response.status);
                console.log(error.response.headers);
                
                // Display error message from API response
                if (error.response.status === 400 && error.response.data && error.response.data.msg) {
                  setRegistrationError(error.response.data.msg);
                } else {
                  setRegistrationError('Registration failed. Please try again.');
                }
              } else if (error.request) {
                console.log(error.request);
                setRegistrationError('Network error. Please check your connection and try again.');
              } else {
                console.log('Error', error.message);
                setRegistrationError('An unexpected error occurred. Please try again.');
              }
            });
      } catch (error) {
        console.error('Error submitting form data:', error);
        setIsSigningUp(false);
      }
    } else {
      setIsSigningUp(false);
    }
    
  };

  return (
      <AppTheme {...props}>
        <CssBaseline enableColorScheme />
        <SignUpContainer direction="column" justifyContent="space-between">
            <Box sx={{ flexGrow: 1 }}>
              <Card variant="outlined">
              <Typography
                sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
              >
                Sign up
              </Typography>
              <Typography variant="h5">
                  Already have an account?{' '}
                  <span>
                    <Link
                      href="/login"
                      variant="h5"
                    >
                      Sign in
                    </Link>
                  </span>
                </Typography>
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <FormControl>
                  <FormLabel htmlFor="name">First name</FormLabel>
                  <TextField
                    autoComplete="firstName"
                    name="first_name"
                    required
                    fullWidth
                    id="first_name"
                    placeholder="First name"
                    error={firstNameError}
                    helperText={firstNameErrorMessage}
                    color={firstNameError ? 'error' : 'primary'}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="name">Last name</FormLabel>
                  <TextField
                    autoComplete="lastName"
                    name="last_name"
                    required
                    fullWidth
                    id="last_name"
                    placeholder="Last name"
                    error={lastNameError}
                    helperText={lastNameErrorMessage}
                    color={lastNameError ? 'error' : 'primary'}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="email">Email</FormLabel>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    placeholder="your@email.com"
                    name="email"
                    autoComplete="email"
                    variant="outlined"
                    error={emailError}
                    helperText={emailErrorMessage}
                    color={passwordError ? 'error' : 'primary'}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="password">Password</FormLabel>
                  <TextField
                    required
                    fullWidth
                    name="password"
                    placeholder="••••••"
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    variant="outlined"
                    error={passwordError}
                    helperText={passwordErrorMessage}
                    color={passwordError ? 'error' : 'primary'}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="password">Confirm Password</FormLabel>
                  <TextField
                    required
                    fullWidth
                    name="confirm_password"
                    placeholder="••••••"
                    type="password"
                    id="confirm_password"
                    autoComplete="new-password"
                    variant="outlined"
                    error={confirmPasswordError}
                    helperText={confirmPasswordErrorMessage}
                    color={confirmPasswordError ? 'error' : 'primary'}
                  />
                </FormControl>
                
                {/* Energy Provider Selection - Hidden when connected */}
                {!isConnected && (
                  <FormControl>
                    <FormLabel htmlFor="select-energy-provider">Select your energy provider</FormLabel>
                    <Select
                      id="select-energy-provider"
                      value={energyProvider}
                      onChange={handleEnergyProviderChange}
                      displayEmpty
                      fullWidth
                      variant="outlined"
                    >
                      <MenuItem value="" disabled>
                        <em>Select Provider</em>
                      </MenuItem>
                      <MenuItem value={'octopus'}>Octopus Energy</MenuItem>
                      {/* <MenuItem value={'other'}>Other</MenuItem> */}
                    </Select>
                  </FormControl>
                )}

                {/* Energy Provider Connection Forms */}
                <Collapse in={energyProvider === 'octopus'}>
                  <Box sx={{ mt: isConnected ? 1 : 0 }}>
                    <ConnectOctopusCard 
                      registrationComplete={registrationComplete}
                      isConnected={isConnected}
                      onConnectionChange={handleConnectionChange}
                    />
                  </Box>
                </Collapse>
                {/* <Collapse in={energyProvider === 'other'}>
                  <ConnectGlowCard 
                    registrationComplete={registrationComplete}
                    isConnected={isConnected}
                    onConnectionChange={handleConnectionChange}
                  />
                </Collapse> */}

                <Typography variant="body1" color="text.primary" align="center">
                  Note that EnergyInfo.uk is currently in early access. We are still testing and improving the service and may make changes to its features and functionality at any time.<br /><br />
                  At the moment, we only support accounts held with Octopus Energy, but we will be adding support for other energy providers in the future.
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      color="primary"
                    />
                  }
                  sx={{ alignSelf: 'center' }}
                  label={
                    <>
                      I accept the{' '}
                      <Link component={RouterLink} to="/terms" target="_blank">
                        terms and conditions
                      </Link>
                      .
                    </>
                  }
                />

                {registrationError && (
                  <Typography variant="error" sx={{ textAlign: 'center', mt: 1 }}>
                    {registrationError}
                  </Typography>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={!isConnected || !termsAccepted || isSigningUp}
                  onClick={validateInputs}
                >
                  {isSigningUp ? 'Signing up...' : 'Sign up'}
                </Button>                
              </Box>
              </Card>
            </Box>
            <Copyright sx={{ mt: 2, mb: 2 }} />
        </SignUpContainer>
      </AppTheme>
  );
}
